import { useContext, useEffect, useMemo, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Dialog } from 'primereact/dialog';
import { goingEventService, listEventsByPlaceAccessKeyService, notGoingEventService } from '../../Service/EventService';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import AuthContext from '../../Contexts/AuthContext';
import { PiCalendar, PiCheckCircleBold, PiClock, PiXCircleBold } from 'react-icons/pi';
import { expandCeremonyTimes } from './expandCeremonyTimes';

require('globalize/lib/cultures/globalize.culture.fr')
// Setup the localizer by providing the moment (or globalize) Object
// to the correct localizer.
const localizer = momentLocalizer(moment); // or globalizeLocalizer

const CalendarComponent = ({ place_access_key, ceremony_times = [] }) => {
  const {userAuth} = useContext(AuthContext)

  const [oneOffEvents, setOneOffEvents] = useState([]);
  const [visibleRange, setVisibleRange] = useState(() => ({
    start: moment().startOf('month').toDate(),
    end: moment().endOf('month').toDate(),
  }));
  const [events, setEvents] = useState([]);
  const [going, setGoing] = useState(false)
  const { t } = useTranslation('events');
  const [visible, setVisible] = useState(false);
  const [eventSelected, setEventSelected] = useState()

  useEffect(() => {
    if (!place_access_key) return;
    listEventsByPlaceAccessKeyService(place_access_key, userAuth?.user?.access_key).then((res) => {
      if (!Array.isArray(res) || !res.length) {
        setOneOffEvents([]);
        return;
      }
      setOneOffEvents(res.map((item) => ({
        id: item.access_key,
        title: item.title,
        description: item.description,
        going: item.going,
        allDay: false,
        isRecurring: false,
        start: new Date(item.date_start.split('T')[0] + 'T' + item.time_start),
        end: new Date(item.date_end.split('T')[0] + 'T' + item.time_end),
      })));
    });
  }, [place_access_key, userAuth?.user?.access_key]);

  const recurringExpanded = useMemo(
    () =>
      expandCeremonyTimes(
        ceremony_times,
        visibleRange.start,
        visibleRange.end,
        t('calendar_component_recurring_title'),
        t('calendar_component_recurring_description')
      ),
    [ceremony_times, visibleRange.start, visibleRange.end, t]
  );

  useEffect(() => {
    setEvents([...oneOffEvents, ...recurringExpanded]);
  }, [oneOffEvents, recurringExpanded]);

  const handleGoing = () => {
    going ? notGoingEventService(going).then(res=>{
      res && setGoing(null)
    }) : goingEventService(userAuth?.user?.access_key,eventSelected?.id).then(res=>{
      res.id && setGoing(res.id)
    })    
  }

  const formats = {
    dateFormat: 'D',
    dayFormat: 'D ddd',
    monthHeaderFormat: 'MMMM YYYY',
    dayHeaderFormat: 'dddd M/D',
    dayRangeHeaderFormat: ({ start, end }, culture, local) =>
      local.format(start, 'MMM D', culture) + ' - ' + local.format(end, 'MMM D', culture),
  };
  const eventStyleGetter = (event, start, end, isSelected) => {
    let backgroundColor = event.isRecurring ? '#1e8449' : '#3174ad';
    if (!event.isRecurring && event.title.includes('Meeting')) {
      backgroundColor = '#8e44ad';
    } else if (!event.isRecurring && event.title.includes('Dinner')) {
      backgroundColor = '#e74c3c';
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '0px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  const handleSelectEvent = (event) => {
    setEventSelected(event)
    setGoing(event.going)
    setVisible(true);
  };

  const handleSelectSlot = (slotInfo) => {
    
  };

  const CustomToolbar = (toolbar) => {
    const goToToday = () => {
      toolbar.onNavigate('TODAY');
    };

    const goToPrevious = () => {
      toolbar.onNavigate('PREV');
    };

    const goToNext = () => {
      toolbar.onNavigate('NEXT');
    };

    const changeView = (view) => {
      toolbar.onView(view);
    };

    return (
      <div className="rbc-toolbar flex flex-column md:flex-row gap-2">
        <Dialog className='w-11 md:w-8' header={eventSelected?.title} visible={visible} onHide={() => {if (!visible) return; setVisible(false); }}>
          {eventSelected && 
          <div className="flex flex-column gap-4">
            <div className='flex flex-column md:flex-row gap-2 align-items-start md:align-items-center'>
              <div className='flex gap-1 align-items-center'>
                <PiCalendar />
                {new Date(eventSelected.start).toLocaleDateString() === new Date(eventSelected.end).toLocaleDateString() ? 
                new Date(eventSelected.start).toLocaleDateString() : 
                <div>{new Date(eventSelected.start).toLocaleDateString()} - {new Date(eventSelected.end).toLocaleDateString()}</div>}
              </div>
              <div className='flex gap-1 align-items-center'>
                <PiClock />
                {eventSelected.isRecurring && !eventSelected.recurringFinish ? (
                  <span>{new Date(eventSelected.start).toLocaleTimeString()}</span>
                ) : new Date(eventSelected.start).toLocaleTimeString() === new Date(eventSelected.end).toLocaleTimeString() ? 
                new Date(eventSelected.start).toLocaleTimeString() : 
                <div>{new Date(eventSelected.start).toLocaleTimeString()} - {new Date(eventSelected.end).toLocaleTimeString()}</div>}
              </div>
            </div>
            <div className=''>{eventSelected.description}</div>
            
            {!eventSelected.isRecurring && (
              <>
                {going ? 
                  <div className='flex text-green-500 gap-1 align-items-center'><PiCheckCircleBold /> {t('calendar_component_going_message')}</div> :
                  <div className='flex text-red-500 gap-1 align-items-center'><PiXCircleBold /> {t('calendar_component_not_going_message')}</div>}
                <Button rounded className='w-max' onClick={handleGoing} label={going ? t('calendar_component_not_going_button') : t('calendar_component_going_button')} icon={going ? 'pi pi-times' : 'pi pi-check'} severity={going ? 'danger' : 'success'} />
              </>
            )}
          </div>}
        </Dialog>

        <span className="rbc-toolbar-label font-bold flex flex-0 text-lg">{toolbar.label}</span>
        
        <span className="rbc-btn-group flex flex-nowrap">
          <button onClick={goToToday} className='flex align-items-center'>{t('calendar_component_today')}</button>
          <button onClick={goToPrevious} className='flex align-items-center'>{t('calendar_component_prev')}</button>
          <button onClick={goToNext} className='flex align-items-center'>{t('calendar_component_next')}</button>
        </span>
        
        <span className="rbc-btn-group flex flex-nowrap">
          <button
            className={toolbar.view === 'month' ? 'rbc-active flex align-items-center' : 'flex align-items-center'}
            onClick={() => changeView('month')}
          >
            {t('calendar_component_month')}
          </button>
          <button
            className={toolbar.view === 'week' ? 'rbc-active flex align-items-center' : 'flex align-items-center'}
            onClick={() => changeView('week')}
          >
            {t('calendar_component_week')}
          </button>
          <button
            className={toolbar.view === 'day' ? 'rbc-active flex align-items-center' : 'flex align-items-center'}
            onClick={() => changeView('day')}
          >
            {t('calendar_component_day')}
          </button>
          <button
            className={toolbar.view === 'agenda' ? 'rbc-active flex align-items-center' : 'flex align-items-center'}
            onClick={() => changeView('agenda')}
          >
            {t('calendar_component_agenda')}
          </button>
        </span>
      </div>
    );
  };

  // Custom Event Component
  const CustomEvent = ({ event }) => {
    let backgroundColor = event.isRecurring ? '#1e8449' : '#3174ad';
    
    const style = {
        backgroundColor,
        borderRadius: '0px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
      }

    return (
      <div style={style}>
        <strong className='text-sm'>{event.title}</strong>
      </div>
    );
  };

  // Custom Agenda Header
  const CustomAgendaHeader = ({ label }) => {
    return (
      <div style={{ backgroundColor: '#f8f9fa', padding: '10px' }}>
        <h3>{label}</h3>
      </div>
    );
  };

  // Custom Day Header
  const CustomDayHeader = ({ label }) => {
    return (
      <div style={{ textAlign: 'center', padding: '5px' }}>
        <strong>{label}</strong>
      </div>
    );
  };

  const components = {
    toolbar: CustomToolbar,
    event: CustomEvent,
    agenda: {
      header: CustomAgendaHeader,
    },
    day: {
      header: CustomDayHeader,
    },
    week: {
      header: CustomDayHeader,
    },
    month: {
      header: CustomDayHeader,
    }
  };


  const handleRangeChange = (range) => {
    if (!range) return;
    if (Array.isArray(range) && range.length > 0) {
      setVisibleRange({ start: range[0], end: range[range.length - 1] });
    } else if (range.start && range.end) {
      setVisibleRange({ start: range.start, end: range.end });
    }
  };

  return (
    <div style={{ height: '100vh' }}>
      <Calendar
        culture='fr'
        components={components}
        eventPropGetter={eventStyleGetter}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        onRangeChange={handleRangeChange}
        selectable={true}
        localizer={localizer}
        formats={formats}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%' }}
        defaultView="month"
        views={['month', 'week', 'day', 'agenda']}
      />
    </div>
  );
};

export default CalendarComponent;